import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { MapPin, ExternalLink } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import Modal from '../components/Modal';
import 'leaflet/dist/leaflet.css';

// Component to invalidate map size when it becomes visible
const MapInvalidator: React.FC = () => {
  const map = useMap();

  useEffect(() => {
    // Small delay to ensure the modal animation is complete
    const timer = setTimeout(() => {
      map.invalidateSize();
    }, 100);

    return () => clearTimeout(timer);
  }, [map]);

  return null;
};

interface Spomenik {
  id: string;
  name: string;
  architect: string;
  year: string;
  country: string;
  city: string;
  coordinates: string;
  lat: number;
  lng: number;
  description: string;
  extendedDescription?: string;
  condition: 'excellent' | 'good' | 'deteriorating' | 'destroyed';
  imageUrl: string;
  galleryImages?: string[];
}

const SpomenikMaps: React.FC = () => {
  const { t } = useTranslation();
  const [selectedCountry, setSelectedCountry] = useState<string>('all');
  const [selectedSpomenik, setSelectedSpomenik] = useState<Spomenik | null>(null);

  const countries = [
    { id: 'all', label: t('spomenik.countries.all') },
    { id: 'serbia', label: t('spomenik.countries.serbia') },
    { id: 'croatia', label: t('spomenik.countries.croatia') },
    { id: 'bosnia', label: t('spomenik.countries.bosnia') },
    { id: 'slovenia', label: t('spomenik.countries.slovenia') },
    { id: 'macedonia', label: t('spomenik.countries.macedonia') },
    { id: 'montenegro', label: t('spomenik.countries.montenegro') },
  ];

  // Sample spomeniks
  const spomeniks: Spomenik[] = [
    {
      id: '1',
      name: t('spomenik.monuments.1.name'),
      architect: 'Bogdan Bogdanović',
      year: '1971',
      country: 'serbia',
      city: 'Jasenovac',
      coordinates: '45.2769° N, 16.9378° E',
      lat: 45.2769,
      lng: 16.9378,
      description: t('spomenik.monuments.1.description'),
      condition: 'good',
      imageUrl: '/spomeniks/monument-1.jpg',
    },
    {
      id: '2',
      name: t('spomenik.monuments.2.name'),
      architect: 'Dušan Džamonja',
      year: '1967',
      country: 'croatia',
      city: 'Podgarić',
      coordinates: '45.4297° N, 15.9756° E',
      lat: 45.4297,
      lng: 15.9756,
      description: t('spomenik.monuments.2.description'),
      condition: 'excellent',
      imageUrl: '/spomeniks/monument-2.jpg',
    },
    {
      id: '3',
      name: t('spomenik.monuments.3.name'),
      architect: 'Miodrag Živković',
      year: '1971',
      country: 'bosnia',
      city: 'Sutjeska',
      coordinates: '43.3564° N, 18.6442° E',
      lat: 43.3564,
      lng: 18.6442,
      description: t('spomenik.monuments.3.description'),
      condition: 'good',
      imageUrl: '/spomeniks/monument-3.jpg',
    },
    {
      id: '4',
      name: t('spomenik.monuments.4.name'),
      architect: 'Jordan and Iskra Grabul',
      year: '1974',
      country: 'macedonia',
      city: 'Kruševo',
      coordinates: '41.3706° N, 21.2489° E',
      lat: 41.3706,
      lng: 21.2489,
      description: t('spomenik.monuments.4.description'),
      condition: 'excellent',
      imageUrl: '/spomeniks/monument-4.jpg',
    },
    {
      id: '5',
      name: t('spomenik.monuments.5.name'),
      architect: 'Vojin Bakić',
      year: '1981',
      country: 'croatia',
      city: 'Kamenska',
      coordinates: '45.2564° N, 16.1547° E',
      lat: 45.2564,
      lng: 16.1547,
      description: t('spomenik.monuments.5.description'),
      condition: 'deteriorating',
      imageUrl: '/spomeniks/monument-5.jpg',
    },
    {
      id: '6',
      name: t('spomenik.monuments.6.name'),
      architect: 'Svetislav Ličina',
      year: '1976',
      country: 'montenegro',
      city: 'Kolašin',
      coordinates: '42.8225° N, 19.5164° E',
      lat: 42.8225,
      lng: 19.5164,
      description: t('spomenik.monuments.6.description'),
      condition: 'good',
      imageUrl: '/spomeniks/monument-6.jpg',
    },
  ];

  const filteredSpomeniks =
    selectedCountry === 'all'
      ? spomeniks
      : spomeniks.filter(spomenik => spomenik.country === selectedCountry);

  const getConditionColor = (condition: string) => {
    switch (condition) {
      case 'excellent':
        return 'bg-green-700';
      case 'good':
        return 'bg-green-500';
      case 'deteriorating':
        return 'bg-yellow-600';
      case 'destroyed':
        return 'bg-[#C41E3A]';
      default:
        return 'bg-gray-600';
    }
  };

  return (
    <>
      <Helmet>
        <title>Spomenik Maps | Interactive Yugoslav Monument Locations</title>
        <meta name="description" content="Explore an interactive map of Spomenik monuments across former Yugoslavia. Discover brutalist monuments, their locations, and historical significance." />
        <link rel="canonical" href="https://borninyu.com/spomenik-maps" />
      </Helmet>
      <div className="min-h-screen bg-white">
        {/* Header */}
        <div className="border-b-4 border-black bg-white p-8 md:p-16">
        <div className="max-w-7xl mx-auto">
          {/* Map Icon */}
          <div className="flex items-center gap-4 mb-6">
            <MapPin className="w-12 h-12" strokeWidth={3} />
            <div className="border-2 border-black px-4 py-2 text-[10px] font-black uppercase">
              {t('spomenik.totalMonuments')}: {spomeniks.length}
            </div>
          </div>

          {/* Title */}
          <h1 className="text-5xl md:text-7xl font-black uppercase leading-none mb-4 tracking-tighter">
            {t('spomenik.title')}
          </h1>

          {/* Subtitle */}
          <p className="text-lg font-bold uppercase text-gray-700 max-w-3xl mb-6">
            {t('spomenik.subtitle')}
          </p>

          {/* Info Box */}
          <div className="border-3 border-black bg-gray-100 p-6 max-w-2xl">
            <div className="text-xs font-bold uppercase leading-relaxed">
              {t('spomenik.infoBox')}
            </div>
          </div>
        </div>
      </div>

      {/* Country Filter */}
      <div className="border-b-3 border-black bg-gray-100 p-4 md:p-6 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap gap-3">
            {countries.map(country => (
              <button
                key={country.id}
                onClick={() => setSelectedCountry(country.id)}
                className={`px-4 py-2 text-xs font-black uppercase border-2 border-black transition-all ${
                  selectedCountry === country.id
                    ? 'bg-black text-white'
                    : 'bg-white text-black hover:bg-gray-200'
                }`}
              >
                {country.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Spomeniks List */}
      <div className="max-w-7xl mx-auto p-8 md:p-16">
        <div className="space-y-8">
          {filteredSpomeniks.map((spomenik, index) => (
            <div
              key={spomenik.id}
              className="border-4 border-black bg-white hover:shadow-[12px_12px_0px_0px_rgba(0,0,0,1)] transition-all cursor-pointer"
              onClick={() => setSelectedSpomenik(spomenik)}
            >
              <div className="grid md:grid-cols-3 gap-0">
                {/* Left: Image */}
                <div className="aspect-square md:aspect-auto bg-gray-200 border-b-4 md:border-b-0 md:border-r-4 border-black relative overflow-hidden flex items-center justify-center">
                  <img
                    src={spomenik.imageUrl}
                    alt={spomenik.name}
                    className="w-full h-full object-contain z-10"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-300 to-gray-400 z-0">
                    <div className="text-8xl font-black text-gray-500 opacity-10">{index + 1}</div>
                  </div>
                  {/* Map Pin Overlay */}
                  <div className="absolute bottom-4 right-4 bg-[#C41E3A] text-white p-3 border-2 border-black z-20">
                    <MapPin className="w-6 h-6" />
                  </div>
                </div>

                {/* Right: Content */}
                <div className="md:col-span-2 p-6 md:p-8">
                  {/* Location Badge */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="border-2 border-black px-3 py-1 text-[10px] font-black uppercase">
                      {t(`spomenik.countries.${spomenik.country}`)}
                    </div>
                    <div
                      className={`${getConditionColor(spomenik.condition)} text-white text-[10px] font-black uppercase px-3 py-1`}
                    >
                      {t(`spomenik.condition.${spomenik.condition}`)}
                    </div>
                  </div>

                  {/* Monument Name */}
                  <h3 className="text-3xl md:text-4xl font-black uppercase leading-tight mb-3 tracking-tighter">
                    {spomenik.name}
                  </h3>

                  {/* Architect & Year */}
                  <div className="text-sm font-bold uppercase text-gray-600 mb-4">
                    {t('spomenik.architect')}: {spomenik.architect} • {t('spomenik.year')}:{' '}
                    {spomenik.year}
                  </div>

                  {/* Description */}
                  <p className="text-sm leading-relaxed mb-6 text-gray-700">
                    {spomenik.description}
                  </p>

                  {/* Location Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[10px] font-bold uppercase">
                    <div className="border border-black px-3 py-2 flex items-center gap-2">
                      <MapPin className="w-3 h-3" />
                      <span>
                        {t('spomenik.city')}: {spomenik.city}
                      </span>
                    </div>
                    <div className="border border-black px-3 py-2">
                      {t('spomenik.coordinates')}: {spomenik.coordinates}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* No Results */}
        {filteredSpomeniks.length === 0 && (
          <div className="text-center py-16">
            <div className="text-4xl font-black uppercase text-gray-400 mb-4">
              {t('spomenik.noResults')}
            </div>
            <p className="text-sm uppercase font-bold text-gray-500">
              {t('spomenik.noResultsDescription')}
            </p>
          </div>
        )}
      </div>

      {/* Footer Legend */}
      <div className="border-t-4 border-black bg-gray-100 p-8">
        <div className="max-w-7xl mx-auto">
          <h4 className="text-xs font-black uppercase mb-4">{t('spomenik.legend.title')}</h4>
          <div className="flex flex-wrap gap-4 text-[10px] font-bold uppercase">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-green-700"></div>
              <span>{t('spomenik.condition.excellent')}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-green-500"></div>
              <span>{t('spomenik.condition.good')}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-yellow-600"></div>
              <span>{t('spomenik.condition.deteriorating')}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-[#C41E3A]"></div>
              <span>{t('spomenik.condition.destroyed')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      <Modal isOpen={!!selectedSpomenik} onClose={() => setSelectedSpomenik(null)}>
        {selectedSpomenik && (
          <div className="grid md:grid-cols-2 gap-0">
            {/* Left: Image & Map */}
            <div className="border-b-4 md:border-b-0 md:border-r-4 border-black">
              {/* Main Image */}
              <div className="aspect-[4/3] bg-gray-200 relative overflow-hidden border-b-4 border-black flex items-center justify-center">
                <img
                  src={selectedSpomenik.imageUrl}
                  alt={selectedSpomenik.name}
                  className="w-full h-full object-contain z-10"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
                <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-300 to-gray-400 z-0">
                  <div className="text-9xl font-black text-gray-500 opacity-10">
                    {selectedSpomenik.id}
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="h-[300px] relative">
                <MapContainer
                  center={[selectedSpomenik.lat, selectedSpomenik.lng]}
                  zoom={13}
                  style={{ height: '100%', width: '100%' }}
                  zoomControl={false}
                >
                  <MapInvalidator />
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <Marker position={[selectedSpomenik.lat, selectedSpomenik.lng]}>
                    <Popup>{selectedSpomenik.name}</Popup>
                  </Marker>
                </MapContainer>

                {/* View on Google Maps Button */}
                <a
                  href={`https://www.google.com/maps?q=${selectedSpomenik.lat},${selectedSpomenik.lng}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="absolute bottom-4 right-4 bg-black text-white px-4 py-2 text-xs font-black uppercase border-4 border-white hover:bg-[#C41E3A] transition-colors flex items-center gap-2 z-[1000]"
                >
                  {t('spomenik.modal.viewOnMaps')}
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>

            {/* Right: Info */}
            <div className="p-8 md:p-12 overflow-y-auto max-h-[80vh]">
              {/* Status Badges */}
              <div className="flex items-center gap-3 mb-6">
                <div className="border-2 border-black px-3 py-1 text-[10px] font-black uppercase">
                  {t(`spomenik.countries.${selectedSpomenik.country}`)}
                </div>
                <div
                  className={`${getConditionColor(selectedSpomenik.condition)} text-white text-[10px] font-black uppercase px-3 py-1`}
                >
                  {t(`spomenik.condition.${selectedSpomenik.condition}`)}
                </div>
              </div>

              {/* Title */}
              <h2 className="text-4xl md:text-5xl font-black uppercase leading-none mb-4 tracking-tighter">
                {selectedSpomenik.name}
              </h2>

              {/* Meta Info */}
              <div className="space-y-3 mb-6 text-sm font-bold uppercase">
                <div className="flex gap-2">
                  <span className="text-gray-500">{t('spomenik.architect')}:</span>
                  <span>{selectedSpomenik.architect}</span>
                </div>
                <div className="flex gap-2">
                  <span className="text-gray-500">{t('spomenik.year')}:</span>
                  <span>{selectedSpomenik.year}</span>
                </div>
                <div className="flex gap-2">
                  <span className="text-gray-500">{t('spomenik.city')}:</span>
                  <span>{selectedSpomenik.city}</span>
                </div>
                <div className="flex gap-2">
                  <span className="text-gray-500">{t('spomenik.coordinates')}:</span>
                  <span className="font-mono text-xs">{selectedSpomenik.coordinates}</span>
                </div>
              </div>

              {/* Description */}
              <div className="border-t-2 border-black pt-6">
                <h3 className="text-xs font-black uppercase mb-3 text-gray-500">
                  {t('spomenik.modal.description')}
                </h3>
                <p className="text-sm leading-relaxed mb-4">{selectedSpomenik.description}</p>
                {selectedSpomenik.extendedDescription && (
                  <p className="text-sm leading-relaxed text-gray-700">
                    {selectedSpomenik.extendedDescription}
                  </p>
                )}
              </div>

              {/* Visiting Info */}
              <div className="border-t-2 border-black mt-6 pt-6">
                <h3 className="text-xs font-black uppercase mb-3 text-gray-500">
                  {t('spomenik.modal.visitingInfo')}
                </h3>
                <p className="text-xs leading-relaxed text-gray-700">
                  {t('spomenik.modal.visitingDescription')}
                </p>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
    </>
  );
};

export default SpomenikMaps;
